import apiClient, { handleApiError } from './apiClient';
import { User } from './authService';
import { Producto } from './productosService';

export interface DireccionEntrega {
  id: number;
  cliente: number;
  nombre_contacto: string;
  telefono: string;
  direccion: string;
  referencia: string;
  latitud: number | null;
  longitud: number | null;
  es_predeterminada: boolean;
}

export interface DireccionEntregaCreate {
  nombre_contacto: string;
  telefono: string;
  direccion: string;
  referencia?: string;
  latitud?: number;
  longitud?: number;
  es_predeterminada?: boolean;
}

export interface DetallePedido {
  id: number;
  producto: number;
  producto_detalle?: Producto;
  cantidad: number;
  precio_unitario: number;
  precio_total: number;
}

export interface DetallePedidoCreate {
  producto: number;
  cantidad: number;
}

export interface PedidoOnline {
  id: number;
  numero_pedido: string;
  cliente: number;
  cliente_detalle?: User;
  direccion_entrega: number;
  direccion_entrega_detalle?: DireccionEntrega;
  detalles: DetallePedido[];
  estado: 'pendiente' | 'confirmado' | 'preparando' | 'en_camino' | 'entregado' | 'cancelado' | 'devuelto' | 'pausado' | 'rechazado';
  subtotal: number;
  descuento: number;
  costo_delivery: number;
  total: number;
  metodo_pago: 'efectivo' | 'tarjeta' | 'yape' | 'plin' | 'transferencia' | 'mercadopago';
  estado_pago: 'pendiente' | 'pagado' | 'fallido' | 'reembolsado';
  repartidor: number | null;
  repartidor_detalle?: User;
  notas: string;
  fecha_pedido: string;
  fecha_confirmacion: string | null;
  fecha_entrega: string | null;
  calificacion: number | null;
  comentario_calificacion: string;
}

export interface PedidoOnlineCreate {
  direccion_entrega: number;
  detalles: DetallePedidoCreate[];
  metodo_pago: 'efectivo' | 'tarjeta' | 'yape' | 'plin' | 'transferencia' | 'mercadopago';
  notas?: string;
}

export interface SeguimientoPedido {
  id: number;
  pedido: number;
  estado: string;
  descripcion: string;
  fecha: string;
  latitud: number | null;
  longitud: number | null;
}

export interface ConfiguracionDelivery {
  id: number;
  radio_cobertura_km: number;
  costo_delivery_base: number;
  costo_por_km_adicional: number;
  pedido_minimo: number;
  horario_inicio: string;
  horario_fin: string;
  activo: boolean;
}

class PedidosService {
  // Direcciones de entrega
  async getDirecciones(): Promise<DireccionEntrega[]> {
    try {
      const response = await apiClient.get<DireccionEntrega[]>('/pedidos/direcciones/');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getDireccion(id: number): Promise<DireccionEntrega> {
    try {
      const response = await apiClient.get<DireccionEntrega>(`/pedidos/direcciones/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createDireccion(data: DireccionEntregaCreate): Promise<DireccionEntrega> {
    try {
      const response = await apiClient.post<DireccionEntrega>('/pedidos/direcciones/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateDireccion(id: number, data: Partial<DireccionEntregaCreate>): Promise<DireccionEntrega> {
    try {
      const response = await apiClient.put<DireccionEntrega>(`/pedidos/direcciones/${id}/`, data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async deleteDireccion(id: number): Promise<void> {
    try {
      await apiClient.delete(`/pedidos/direcciones/${id}/`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async setDireccionPredeterminada(id: number): Promise<DireccionEntrega> {
    try {
      const response = await apiClient.post<DireccionEntrega>(
        `/pedidos/direcciones/${id}/set_predeterminada/`
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Pedidos
  async getPedidos(params?: {
    estado?: string;
    fecha_desde?: string;
    fecha_hasta?: string;
  }): Promise<PedidoOnline[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.estado) queryParams.append('estado', params.estado);
      if (params?.fecha_desde) queryParams.append('fecha_desde', params.fecha_desde);
      if (params?.fecha_hasta) queryParams.append('fecha_hasta', params.fecha_hasta);
      
      const url = `/pedidos/pedidos/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get<PedidoOnline[]>(url);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async getPedido(id: number): Promise<PedidoOnline> {
    try {
      const response = await apiClient.get<PedidoOnline>(`/pedidos/pedidos/${id}/`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async createPedido(data: PedidoOnlineCreate): Promise<PedidoOnline> {
    try {
      const response = await apiClient.post<PedidoOnline>('/pedidos/pedidos/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateEstadoPedido(
    id: number,
    estado: PedidoOnline['estado'],
    descripcion?: string
  ): Promise<PedidoOnline> {
    try {
      const response = await apiClient.post<PedidoOnline>(
        `/pedidos/pedidos/${id}/actualizar_estado/`,
        { estado, descripcion }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async asignarRepartidor(pedidoId: number, repartidorId: number): Promise<PedidoOnline> {
    try {
      const response = await apiClient.post<PedidoOnline>(
        `/pedidos/pedidos/${pedidoId}/asignar_repartidor/`,
        { repartidor_id: repartidorId }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async calificarPedido(
    id: number,
    calificacion: number,
    comentario?: string
  ): Promise<PedidoOnline> {
    try {
      const response = await apiClient.post<PedidoOnline>(
        `/pedidos/pedidos/${id}/calificar/`,
        { calificacion, comentario }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async cancelarPedido(id: number, motivo?: string): Promise<PedidoOnline> {
    try {
      const response = await apiClient.post<PedidoOnline>(
        `/pedidos/pedidos/${id}/cancelar/`,
        { motivo }
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Seguimiento
  async getSeguimiento(pedidoId: number): Promise<SeguimientoPedido[]> {
    try {
      const response = await apiClient.get<SeguimientoPedido[]>(
        `/pedidos/pedidos/${pedidoId}/seguimiento/`
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async agregarSeguimiento(
    pedidoId: number,
    data: {
      descripcion: string;
      latitud?: number;
      longitud?: number;
    }
  ): Promise<SeguimientoPedido> {
    try {
      const response = await apiClient.post<SeguimientoPedido>(
        `/pedidos/pedidos/${pedidoId}/agregar_seguimiento/`,
        data
      );
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Configuración Delivery
  async getConfiguracion(): Promise<ConfiguracionDelivery> {
    try {
      const response = await apiClient.get<ConfiguracionDelivery>('/pedidos/configuracion/');
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  async updateConfiguracion(data: Partial<ConfiguracionDelivery>): Promise<ConfiguracionDelivery> {
    try {
      const response = await apiClient.put<ConfiguracionDelivery>('/pedidos/configuracion/', data);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Mis pedidos (para cliente)
  async getMisPedidos(params?: {
    estado?: string;
    fecha_desde?: string;
    fecha_hasta?: string;
  }): Promise<PedidoOnline[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.estado) queryParams.append('estado', params.estado);
      if (params?.fecha_desde) queryParams.append('fecha_desde', params.fecha_desde);
      if (params?.fecha_hasta) queryParams.append('fecha_hasta', params.fecha_hasta);
      
      const url = `/pedidos/mis-pedidos/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get<PedidoOnline[]>(url);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Pedidos asignados (para repartidor)
  async getPedidosAsignados(params?: {
    estado?: string;
  }): Promise<PedidoOnline[]> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.estado) queryParams.append('estado', params.estado);
      
      const url = `/pedidos/mis-entregas/${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await apiClient.get<PedidoOnline[]>(url);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

const pedidosService = new PedidosService();
export { pedidosService };
export default pedidosService;
